import { migrateTraversal } from "../helpers/migrateTraversal";
import { cloneDeep } from "lodash";
import { IWebsiteBuilderMigrationNode, IWebsiteBuilderSchemaVersionMigrationNode } from "../helpers/migrationNode";
import { WebsiteBuilderMigrationError } from "../helpers/migrationError";
import { WEBSITE_BUILDER_DESIGN_SPACE_BOUNDARY_TABLET_WIDTH, WEBSITE_BUILDER_DESIGN_SPACE_BOUNDARY_MOBILE_WIDTH } from "../../../website/shared/device";

type TMigratePosition = {
  x: number;
  y: number;
}

type TMigrateDimensions = {
  width: number;
  height: number;
}

type TMigrateLayoutData = {
  position: TMigratePosition;
  dimensions: TMigrateDimensions;
}

type TMigrateLayoutV2Input = {
  desktop: TMigrateLayoutData;
}

type TMigrateLayoutV3Result = {
  desktop: TMigrateLayoutData;
  tablet: TMigrateLayoutData;
  mobile: TMigrateLayoutData;
}

export interface IMigrateElementV2Input extends IWebsiteBuilderMigrationNode {
  type: "element";
  element: {
    layout: TMigrateLayoutV2Input;
  }
}

export interface IMigrateElementV3Result extends IWebsiteBuilderMigrationNode {
  type: "element";
  element: {
    layout: TMigrateLayoutV3Result;
  }
}

function isMigrateElementV2Input(node: any): node is IMigrateElementV2Input {
  const partialNode: Partial<IMigrateElementV2Input> = node;

  return (
    partialNode?.type === "element" &&
    partialNode.element?.layout?.desktop?.position?.x != null &&
    partialNode.element?.layout?.desktop?.position?.y != null &&
    partialNode.element?.layout?.desktop?.dimensions?.width != null &&
    partialNode.element?.layout?.desktop?.dimensions?.height != null
  );
}

export interface IMigrateContainerV2Input extends IWebsiteBuilderMigrationNode {
  type: "container";
  container: {
    layout: TMigrateLayoutV2Input;
  }
  children: IWebsiteBuilderMigrationNode[];
}

export interface IMigrateContainerV3Result extends IWebsiteBuilderMigrationNode {
  type: "container";
  container: {
    layout: TMigrateLayoutV3Result;
  }
  children: IWebsiteBuilderMigrationNode[];
}

function isMigrateContainerV2Input(node: any): node is IMigrateContainerV2Input {
  const partialNode: Partial<IMigrateContainerV2Input> = node;

  return (
    partialNode?.type === "container" &&
    partialNode.container?.layout?.desktop?.position?.x != null &&
    partialNode.container?.layout?.desktop?.position?.y != null &&
    partialNode.container?.layout?.desktop?.dimensions?.width != null &&
    partialNode.container?.layout?.desktop?.dimensions?.height != null
  );
}

export function migrateSchemaV2ToV3(node: IWebsiteBuilderSchemaVersionMigrationNode): IWebsiteBuilderSchemaVersionMigrationNode {
  if (node.schemaVersion !== 2) {
    throw new WebsiteBuilderMigrationError({
      type: "cannot-migrate-from-schema-version",
      schemaVersion: node.schemaVersion,
      nextSchemaVersion: 3,
    });
  }

  function migrateNode(prev: IWebsiteBuilderMigrationNode): IWebsiteBuilderMigrationNode {
    if (prev?.type === "element") {
      if (!isMigrateElementV2Input(prev)) {
        throw new WebsiteBuilderMigrationError({
          type: "element-missing-field",
          schemaVersion: 2,
          nextSchemaVersion: 3,
        });
      }
      
      const elementBaseV3Result: IMigrateElementV2Input = cloneDeep(prev);

      const elementLayoutV3Result: TMigrateLayoutV3Result = {
        desktop: {
          position: { ...elementBaseV3Result.element.layout.desktop.position },
          dimensions: { ...elementBaseV3Result.element.layout.desktop.dimensions },
        },
        tablet: {
          position: { x: 0, y: 0 },
          dimensions: {
            width: Math.min(elementBaseV3Result.element.layout.desktop.dimensions.width, WEBSITE_BUILDER_DESIGN_SPACE_BOUNDARY_TABLET_WIDTH),
            height: elementBaseV3Result.element.layout.desktop.dimensions.height,
          },
        },
        mobile: {
          position: { x: 0, y: 0 },
          dimensions: {
            width: Math.min(elementBaseV3Result.element.layout.desktop.dimensions.width, WEBSITE_BUILDER_DESIGN_SPACE_BOUNDARY_MOBILE_WIDTH),
            height: elementBaseV3Result.element.layout.desktop.dimensions.height,
          },
        },
      };

      const elementV3Result: IMigrateElementV3Result = {
        ...elementBaseV3Result,
        element: {
          ...elementBaseV3Result.element,
          layout: elementLayoutV3Result,
        },
      };
      
      return elementV3Result;
    } else if (prev?.type === "container") {
      if (!isMigrateContainerV2Input(prev)) {
        throw new WebsiteBuilderMigrationError({
          type: "container-missing-field",
          schemaVersion: 2,
          nextSchemaVersion: 3,
        });
      }
      
      const containerBaseV3Result: IMigrateContainerV2Input = cloneDeep(prev);

      const containerLayoutV3Result: TMigrateLayoutV3Result = {
        desktop: {
          position: { ...containerBaseV3Result.container.layout.desktop.position },
          dimensions: { ...containerBaseV3Result.container.layout.desktop.dimensions },
        },
        tablet: {
          position: { x: 0, y: 0 },
          dimensions: { ...containerBaseV3Result.container.layout.desktop.dimensions },
        },
        mobile: {
          position: { x: 0, y: 0 },
          dimensions: { ...containerBaseV3Result.container.layout.desktop.dimensions },
        },
      };

      const containerV3Result: IMigrateContainerV3Result = {
        ...containerBaseV3Result,
        container: {
          ...containerBaseV3Result.container,
          layout: containerLayoutV3Result,
        },
      };

      return containerV3Result;
    } else {
      return prev;
    }
  }

  return {
    ...migrateTraversal(node, migrateNode),
    schemaVersion: 3,
  };
}